/**
 * The order page's change dialogs (docs/features/order-to-invoice/
 * 16_frontend_followup_spec.md §1):
 * - Add extra: a service, priced by hand, as a new line on this order — for
 *   the whole order, for some of its lines, or for one delivery — with the
 *   customer's agreement noted. Its choices (service kinds, lines,
 *   deliveries) and their labels are the order view's `add_extra`; it posts
 *   to the catalogue's route that view names. A kind that `can_be_bought`
 *   (fulfilment, installation; supplier interface spec 12) is done in-house
 *   or by a supplier, searched and with its cost entered.
 * - Add to project: one of the payer's projects, or a new one titled from
 *   the backend's suggestion (editable).
 * - Contact a supplier: log a call, from a late row in the action box.
 * - The Payment step's (step 3a): Change payment method, Set contact, Record
 *   PO number, and Contact {customer} once their reminders have run out.
 * - The Invoice step's (sales ledger SL2): Set VAT on a line held for want of a
 *   VAT treatment (finance).
 * - The Payment step's pro-forma (sales ledger SL3; round 19's
 *   `RecordPaymentDialog`): Record payment, a bank transfer received (finance),
 *   also opened from Finance › Awaiting payment.
 * - Cancel the rest (doc 98): a part-sent line's rest, worded by the backend.
 * - Cancel line (3c): why, then confirm; from the order page.
 *
 * Each checks its inputs beside the field as typed; the server's refusal,
 * bound to the field it names, replaces those on submit, with a banner that
 * counts them. `onSubmit` resolves to the refusal to show, or null once done.
 */
import { useId, useState, type ReactNode } from "react";
import type {
  PortalContactChoice,
  PortalContactSuggestions,
} from "@shared-core/data/customer-portal-access-types";
import type {
  AddExtra,
  AddExtraService,
  AddServiceExtraRequest,
  AddToProjectRequest,
  CancelLineRequest,
  CancelRestView,
  ChangePaymentMethodRequest,
  ContactParty,
  CustomerContactParty,
  CustomerOrderNumberRequest,
  ExtraAppliesTo,
  LogContactRequest,
  LogSupplierCallRequest,
  OrderContact,
  OrderProjectRef,
  PaymentMethodChoice,
  RecordProformaPaymentRequest,
  RecordTharsternJobRequest,
  ServiceKind,
  SetOrderContactRequest,
  SetVatTreatmentRequest,
  VatTreatmentOption,
} from "@shared-core/data/order-to-invoice-types";
import {
  formatQuotedPrice,
  formatShortDateOrDash,
} from "@shared-core/domain/formatters";
import { newUuid } from "@shared-core/domain/object-ui/command-run";
import {
  londonTodayIso,
  type OrderStepFailure,
  type OrderStepField,
} from "@shared-core/domain/order-to-invoice-presentation";
import { PortalContactPicker } from "../../patterns/PortalContactPicker";
import { Alert, AlertDescription } from "../../primitives/alert";
import { Button } from "../../primitives/button";
import { Checkbox } from "../../primitives/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../primitives/dialog";
import { Input } from "../../primitives/input";
import { Label } from "../../primitives/label";
import { RadioGroup, RadioGroupItem } from "../../primitives/radio-group";
import { Textarea } from "../../primitives/textarea";
import type { EntityOption } from "../../patterns/EntityCombobox";
import {
  CHOOSE_SUPPLIER,
  ENTER_COST,
  SERVICE_GOODS_HINT,
  ServiceSupplierFields,
} from "./ServiceSupplierFields";
import {
  costPenceOf,
  withoutSupplierRefusal,
} from "@shared-core/domain/service-supplier";
import {
  applicableParameters,
  parameterProblems,
  parametersBody,
} from "@shared-core/domain/service-parameters";
import { ServiceParameterFields } from "./ServiceParameterFields";

/** "25", "25.5" or "25.50" → 2500 pence; null when it isn't a price. */
function penceOf(pounds: string): number | null {
  const text = pounds.trim().replace(/^£/, "").replace(/,/g, "");
  if (!/^\d+(\.\d{1,2})?$/.test(text)) return null;
  return Math.round(Number(text) * 100);
}

/** The route's bounds (the catalogue's Add extra body). */
const MAX_EXTRA_QUANTITY = 10_000_000;
const MAX_EXTRA_PRICE_PENCE = 100_000_000;
const MAX_PROJECT_TITLE_LENGTH = 120;

/** One labelled input with its message beside it, wired for assistive tech. */
export function Field({
  id,
  label,
  error,
  hint,
  children,
}: {
  id: string;
  label: string;
  error?: string;
  hint?: ReactNode;
  children: (aria: {
    id: string;
    "aria-invalid": true | undefined;
    "aria-describedby": string | undefined;
  }) => ReactNode;
}) {
  const errorId = `${id}-error`;
  const hintId = `${id}-hint`;
  const describedBy =
    [error ? errorId : null, hint ? hintId : null].filter(Boolean).join(" ") ||
    undefined;
  return (
    <div className="space-y-1">
      <Label htmlFor={id}>{label}</Label>
      {children({
        id,
        "aria-invalid": error ? true : undefined,
        "aria-describedby": describedBy,
      })}
      {hint && (
        <p id={hintId} className="text-xs text-muted-foreground">
          {hint}
        </p>
      )}
      {error && (
        <p id={errorId} className="text-sm text-red-600">
          {error}
        </p>
      )}
    </div>
  );
}

export function FailureBanner({
  failure,
}: {
  failure: OrderStepFailure | null;
}) {
  if (!failure) return null;
  return (
    <Alert variant="destructive" role="alert">
      <AlertDescription>{failure.summary}</AlertDescription>
    </Alert>
  );
}

type Problems = Partial<Record<OrderStepField, string>>;

// ── Add extra ─────────────────────────────────────────────────────────────

type ExtraFor = ExtraAppliesTo["kind"];

interface ExtraValues {
  forKind: ExtraFor;
  lineRefs: string[];
  deliveryLineId: string;
  quantity: string;
  price: string;
  agreementNote: string;
}

/** What is checked as typed: the same rules the server applies. */
function extraProblems(values: ExtraValues): Problems {
  const quantity = Number(values.quantity);
  const pence = penceOf(values.price);
  return {
    applies_to:
      values.forKind === "order_lines" && values.lineRefs.length === 0
        ? "Pick at least one order line."
        : values.forKind === "delivery_line" && !values.deliveryLineId
          ? "Pick the delivery."
          : undefined,
    quantity:
      values.quantity.trim() === "" ||
      !Number.isInteger(quantity) ||
      quantity < 1 ||
      quantity > MAX_EXTRA_QUANTITY
        ? "Enter a whole number from 1 to 10,000,000."
        : undefined,
    price_pence:
      pence === null || pence > MAX_EXTRA_PRICE_PENCE
        ? "Enter a price in pounds, e.g. 25.00."
        : undefined,
    agreement_note: values.agreementNote.trim()
      ? undefined
      : "Note how the customer agreed: who, when and how.",
  };
}

/** The service's own rules as typed: the dictionary checks the same on submit. */
function serviceProblems(
  service: AddExtraService | undefined,
  activities: string[],
  parameters: Record<string, string>,
): Problems {
  if (!service) return { service_kind: "Pick a kind." };
  return {
    activities:
      activities.length < service.min_activities
        ? "Tick what is done."
        : undefined,
    ...parameterProblems(
      applicableParameters(service.parameters, activities),
      parameters,
    ),
  };
}

/** The first "For" choice the service allows. */
function firstForOf(service: AddExtraService | undefined): ExtraFor {
  if (!service || service.applies_to.includes("order")) return "order";
  return service.applies_to.includes("order_line")
    ? "order_lines"
    : "delivery_line";
}

function appliesToOf(values: ExtraValues): ExtraAppliesTo {
  switch (values.forKind) {
    case "order_lines":
      return { kind: "order_lines", line_refs: values.lineRefs };
    case "delivery_line":
      return { kind: "delivery_line", delivery_line_id: values.deliveryLineId };
    default:
      return { kind: "order" };
  }
}

export function AddExtraDialog({
  orderNo,
  addExtra,
  busy,
  onClose,
  onAdd,
}: {
  orderNo: string;
  /** The order view's Add extra: its choices, worded by the backend. */
  addExtra: AddExtra;
  busy: boolean;
  onClose: () => void;
  onAdd: (input: AddServiceExtraRequest) => Promise<OrderStepFailure | null>;
}) {
  const id = useId();
  // One request id per dialog opening, reused on a retry: the extra is added once.
  const [requestId] = useState(newUuid);
  const [serviceKind, setServiceKind] = useState<ServiceKind | "">(
    addExtra.services[0]?.service_kind ?? "",
  );
  const service = addExtra.services.find((s) => s.service_kind === serviceKind);
  const [activities, setActivities] = useState<string[]>([]);
  const [parameters, setParameters] = useState<Record<string, string>>({});
  const [values, setValues] = useState<ExtraValues>({
    forKind: firstForOf(addExtra.services[0]),
    lineRefs: [],
    deliveryLineId: addExtra.targets.delivery_lines[0]?.delivery_line_id ?? "",
    quantity: "1",
    price: "",
    agreementNote: "",
  });
  // A fulfilment or installation may be bought from a supplier, at a cost entered by hand (spec 12).
  const [doneBy, setDoneBy] = useState<"in_house" | "supplier">("in_house");
  const [supplier, setSupplier] = useState<EntityOption | null>(null);
  const [cost, setCost] = useState("");
  const isBought = Boolean(service?.can_be_bought) && doneBy === "supplier";
  const [touched, setTouched] = useState(false);
  const [failure, setFailure] = useState<OrderStepFailure | null>(null);
  // Once the CSR types a quantity, choosing lines no longer overwrites it.
  const [quantityTyped, setQuantityTyped] = useState(false);
  const quantityOfLine = new Map(
    addExtra.targets.lines.map((l) => [l.line_ref, l.quantity]),
  );
  /** A mailing's or postage's quantity defaults to the largest item line it is for (spec 10). */
  const prefillOf = (v: ExtraValues, s: AddExtraService | undefined) =>
    s?.quantity_from_targets &&
    v.forKind === "order_lines" &&
    v.lineRefs.length > 0
      ? Math.max(...v.lineRefs.map((ref) => quantityOfLine.get(ref) ?? 0))
      : null;
  const set = (
    patch: Partial<ExtraValues>,
    forService: AddExtraService | undefined = service,
  ) =>
    setValues((current) => {
      const next = { ...current, ...patch };
      // A change that sets the quantity itself (the CSR typing) is never overwritten, even before
      // quantityTyped's update lands.
      const prefill =
        quantityTyped || "quantity" in patch
          ? null
          : prefillOf(next, forService);
      return prefill ? { ...next, quantity: String(prefill) } : next;
    });
  const isPrefilled = !quantityTyped && prefillOf(values, service) !== null;

  const problems: Problems = {
    ...extraProblems(values),
    ...serviceProblems(service, activities, parameters),
    ...(isBought
      ? {
          supplier_no: supplier ? undefined : CHOOSE_SUPPLIER,
          cost_pence: costPenceOf(cost) === null ? ENTER_COST : undefined,
        }
      : {}),
  };
  const hasProblem = Object.values(problems).some(Boolean) || !serviceKind;
  const errorOf = (field: OrderStepField) =>
    (touched ? problems[field] : undefined) ?? failure?.fieldErrors[field];
  const forError = errorOf("applies_to");
  const forErrorId = `${id}-for-error`;
  const { lines, delivery_lines: deliveries } = addExtra.targets;
  const allows = (target: AddExtraService["applies_to"][number]) =>
    service?.applies_to.includes(target) ?? false;
  const activitiesError = errorOf("activities");
  const activitiesErrorId = `${id}-activities-error`;
  const chooseKind = (kind: ServiceKind) => {
    const chosen = addExtra.services.find((s) => s.service_kind === kind);
    setServiceKind(kind);
    setActivities([]);
    setParameters({});
    setDoneBy("in_house");
    setSupplier(null);
    setCost("");
    set(
      {
        forKind: firstForOf(chosen),
        lineRefs: [],
      },
      chosen,
    );
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Add extra to {orderNo}</DialogTitle>
          <DialogDescription>
            A service, priced by hand, as a new line on this order.
          </DialogDescription>
        </DialogHeader>
        <form
          className="space-y-3"
          onSubmit={async (e) => {
            e.preventDefault();
            setTouched(true);
            setFailure(null);
            if (hasProblem || !serviceKind || !service) return;
            const refused = await onAdd({
              request_id: requestId,
              service_kind: serviceKind,
              activities,
              parameters: parametersBody(
                applicableParameters(service.parameters, activities),
                parameters,
              ),
              applies_to: appliesToOf(values),
              quantity: Number(values.quantity),
              price_pence: penceOf(values.price)!,
              agreement_note: values.agreementNote.trim(),
              ...(isBought && supplier
                ? { supplier_no: supplier.id, cost_pence: costPenceOf(cost)! }
                : {}),
            });
            setFailure(refused);
            if (!refused) onClose();
          }}
        >
          <FailureBanner failure={failure} />
          <Field id={`${id}-kind`} label="Kind" error={errorOf("service_kind")}>
            {(aria) => (
              <select
                {...aria}
                value={serviceKind}
                onChange={(e) => chooseKind(e.target.value as ServiceKind)}
                className="h-9 w-full rounded-md border border-border bg-card px-3 text-sm"
              >
                {addExtra.services.map((s) => (
                  <option key={s.service_kind} value={s.service_kind}>
                    {s.label}
                  </option>
                ))}
              </select>
            )}
          </Field>
          {service && service.activities.length > 0 && (
            <fieldset
              className="space-y-1.5"
              aria-invalid={activitiesError ? true : undefined}
              aria-describedby={activitiesError ? activitiesErrorId : undefined}
            >
              <legend className="text-sm font-medium">What is done</legend>
              <div
                role="group"
                aria-label="What is done"
                className="grid gap-1.5 rounded-md border border-border p-2 sm:grid-cols-2"
              >
                {service.activities.map((a) => {
                  const checkboxId = `${id}-activity-${a.code}`;
                  return (
                    <div key={a.code} className="flex items-center gap-2">
                      <Checkbox
                        id={checkboxId}
                        checked={activities.includes(a.code)}
                        aria-invalid={activitiesError ? true : undefined}
                        onCheckedChange={(checked) =>
                          setActivities((current) =>
                            checked === true
                              ? [...current, a.code]
                              : current.filter((c) => c !== a.code),
                          )
                        }
                      />
                      <Label htmlFor={checkboxId} className="font-normal">
                        {a.label}
                      </Label>
                    </div>
                  );
                })}
              </div>
              {activitiesError && (
                <p id={activitiesErrorId} className="text-sm text-red-600">
                  {activitiesError}
                </p>
              )}
            </fieldset>
          )}
          {service && (
            <ServiceParameterFields
              idPrefix={id}
              parameters={applicableParameters(service.parameters, activities)}
              values={parameters}
              errorOf={(key) => errorOf(`parameters.${key}`)}
              onChange={(key, value) =>
                setParameters((current) => ({ ...current, [key]: value }))
              }
            />
          )}
          <fieldset
            className="space-y-2"
            aria-invalid={forError ? true : undefined}
            aria-describedby={forError ? forErrorId : undefined}
          >
            <legend className="text-sm font-medium">For</legend>
            <RadioGroup
              value={values.forKind}
              onValueChange={(v) => set({ forKind: v as ExtraFor })}
              aria-label="For"
            >
              {allows("order") && (
                <div className="flex items-center gap-2">
                  <RadioGroupItem value="order" id={`${id}-for-order`} />
                  <Label htmlFor={`${id}-for-order`}>The whole order</Label>
                </div>
              )}
              {allows("order_line") && lines.length > 0 && (
                <div className="flex items-center gap-2">
                  <RadioGroupItem value="order_lines" id={`${id}-for-lines`} />
                  <Label htmlFor={`${id}-for-lines`}>Order lines</Label>
                </div>
              )}
              {allows("delivery_line") && deliveries.length > 0 && (
                <div className="flex items-center gap-2">
                  <RadioGroupItem
                    value="delivery_line"
                    id={`${id}-for-delivery`}
                  />
                  <Label htmlFor={`${id}-for-delivery`}>A delivery</Label>
                </div>
              )}
            </RadioGroup>
            {values.forKind === "order_lines" && (
              <div
                role="group"
                aria-label="Order lines"
                className="space-y-1.5 rounded-md border border-border p-2"
              >
                {lines.map((l) => {
                  const checkboxId = `${id}-line-${l.line_ref}`;
                  return (
                    <div key={l.line_ref} className="flex items-center gap-2">
                      <Checkbox
                        id={checkboxId}
                        checked={values.lineRefs.includes(l.line_ref)}
                        aria-invalid={forError ? true : undefined}
                        onCheckedChange={(checked) =>
                          set({
                            lineRefs:
                              checked === true
                                ? [...values.lineRefs, l.line_ref]
                                : values.lineRefs.filter(
                                    (r) => r !== l.line_ref,
                                  ),
                          })
                        }
                      />
                      <Label htmlFor={checkboxId} className="font-normal">
                        {l.label}
                      </Label>
                    </div>
                  );
                })}
              </div>
            )}
            {values.forKind === "delivery_line" && (
              <select
                aria-label="Delivery"
                aria-invalid={forError ? true : undefined}
                value={values.deliveryLineId}
                onChange={(e) => set({ deliveryLineId: e.target.value })}
                className="h-9 w-full rounded-md border border-border bg-card px-3 text-sm"
              >
                {deliveries.map((d) => (
                  <option key={d.delivery_line_id} value={d.delivery_line_id}>
                    {d.label}
                  </option>
                ))}
              </select>
            )}
            {forError && (
              <p id={forErrorId} className="text-sm text-red-600">
                {forError}
              </p>
            )}
          </fieldset>
          {service?.can_be_bought && (
            <fieldset className="space-y-2">
              <legend className="text-sm font-medium">Done by</legend>
              <RadioGroup
                value={doneBy}
                onValueChange={(v) => setDoneBy(v as "in_house" | "supplier")}
                aria-label="Done by"
              >
                <div className="flex items-center gap-2">
                  <RadioGroupItem value="in_house" id={`${id}-in-house`} />
                  <Label htmlFor={`${id}-in-house`}>In-house</Label>
                </div>
                <div className="flex items-center gap-2">
                  <RadioGroupItem
                    value="supplier"
                    id={`${id}-supplier-choice`}
                  />
                  <Label htmlFor={`${id}-supplier-choice`}>A supplier</Label>
                </div>
              </RadioGroup>
              {isBought && (
                <ServiceSupplierFields
                  idPrefix={id}
                  suppliersUrl={addExtra.suppliers_url}
                  supplier={supplier}
                  onSupplierChange={(chosen) => {
                    setSupplier(chosen);
                    setFailure(withoutSupplierRefusal);
                  }}
                  cost={cost}
                  onCostChange={setCost}
                  supplierError={errorOf("supplier_no")}
                  costError={errorOf("cost_pence")}
                  goodsHint={
                    values.forKind === "order" ? undefined : SERVICE_GOODS_HINT
                  }
                  disabled={busy}
                />
              )}
            </fieldset>
          )}
          <div className="grid gap-3 sm:grid-cols-2">
            <Field
              id={`${id}-quantity`}
              label="Quantity"
              error={errorOf("quantity")}
              hint={isPrefilled ? "From the largest line chosen." : undefined}
            >
              {(aria) => (
                <Input
                  {...aria}
                  inputMode="numeric"
                  value={values.quantity}
                  onChange={(e) => {
                    setQuantityTyped(true);
                    set({ quantity: e.target.value });
                  }}
                />
              )}
            </Field>
            <Field
              id={`${id}-price`}
              label="Manual price, ex VAT (£)"
              error={errorOf("price_pence")}
            >
              {(aria) => (
                <Input
                  {...aria}
                  inputMode="decimal"
                  value={values.price}
                  placeholder="25.00"
                  onChange={(e) => set({ price: e.target.value })}
                />
              )}
            </Field>
          </div>
          <Field
            id={`${id}-agreement`}
            label="Customer agreement"
            error={errorOf("agreement_note")}
            hint="Who agreed, when and how, e.g. “Agreed with Steve by phone”."
          >
            {(aria) => (
              <Textarea
                {...aria}
                rows={2}
                value={values.agreementNote}
                onChange={(e) => set({ agreementNote: e.target.value })}
              />
            )}
          </Field>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={busy}>
              Add extra
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ── Add to project ────────────────────────────────────────────────────────

const NEW_PROJECT = "__new__";

/** "PRJ-D30515 · Travelodge AW26": a project as a choice names it. */
const projectChoiceLabel = (p: OrderProjectRef) =>
  `${p.project_no} · ${p.title}`;

/** Add to project…: one of the payer's projects, or a new one, titled from the suggestion. */
export function AddToProjectDialog({
  orderNo,
  projects,
  suggestion,
  busy,
  onClose,
  onAdd,
}: {
  orderNo: string;
  /** The payer's projects; a new one is always on offer. */
  projects: OrderProjectRef[];
  /** The backend's title for a new project, pre-filled and editable. */
  suggestion: string;
  busy: boolean;
  onClose: () => void;
  onAdd: (input: AddToProjectRequest) => Promise<OrderStepFailure | null>;
}) {
  const id = useId();
  const [choice, setChoice] = useState(projects[0]?.project_no ?? NEW_PROJECT);
  const [title, setTitle] = useState(suggestion);
  const [failure, setFailure] = useState<OrderStepFailure | null>(null);
  const isNew = choice === NEW_PROJECT;
  const clean = title.trim();
  const titleProblem = !isNew
    ? undefined
    : !clean
      ? "Name the project."
      : clean.length > MAX_PROJECT_TITLE_LENGTH
        ? "Keep the title to 120 characters."
        : undefined;
  // The instant check: a typed title one of the payer's projects already has.
  // Feedback only; the backend decides.
  const sameTitle = isNew
    ? projects.find((p) => p.title.trim().toLowerCase() === clean.toLowerCase())
    : undefined;
  const projectError = failure?.fieldErrors.project_no;
  const titleError = failure?.fieldErrors.new_project_title ?? titleProblem;
  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add {orderNo} to a project</DialogTitle>
          <DialogDescription>
            A project groups the payer's orders. Project pages are coming later.
          </DialogDescription>
        </DialogHeader>
        <form
          className="space-y-3"
          onSubmit={async (e) => {
            e.preventDefault();
            setFailure(null);
            if (titleProblem) return;
            const refused = await onAdd(
              isNew ? { new_project_title: clean } : { project_no: choice },
            );
            setFailure(refused);
            if (!refused) onClose();
          }}
        >
          <FailureBanner failure={failure} />
          <RadioGroup
            value={choice}
            onValueChange={setChoice}
            aria-label="Project"
            aria-invalid={projectError && !isNew ? true : undefined}
            aria-describedby={
              projectError && !isNew ? `${id}-project-error` : undefined
            }
          >
            {projects.map((p) => (
              <div key={p.project_no} className="flex items-center gap-2">
                <RadioGroupItem
                  value={p.project_no}
                  id={`${id}-${p.project_no}`}
                />
                <Label htmlFor={`${id}-${p.project_no}`}>
                  {projectChoiceLabel(p)}
                </Label>
              </div>
            ))}
            <div className="flex items-center gap-2">
              <RadioGroupItem value={NEW_PROJECT} id={`${id}-new`} />
              <Label htmlFor={`${id}-new`}>New project</Label>
            </div>
          </RadioGroup>
          {isNew ? (
            <Field
              id={`${id}-title`}
              label="Project title"
              error={titleError}
              hint={
                sameTitle && (
                  <>
                    The payer already has this project.{" "}
                    <button
                      type="button"
                      className="underline"
                      onClick={() => setChoice(sameTitle.project_no)}
                    >
                      Pick {sameTitle.project_no} instead?
                    </button>
                  </>
                )
              }
            >
              {(aria) => (
                <Input
                  {...aria}
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                />
              )}
            </Field>
          ) : (
            projectError && (
              <p id={`${id}-project-error`} className="text-sm text-red-600">
                {projectError}
              </p>
            )
          )}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={busy || !!titleProblem}>
              Add to project
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ── Contact a supplier ────────────────────────────────────────────────────

/**
 * Contact {supplier}, from a late row in the action box (doc 27 §3): what is
 * outstanding with them on this order, then a logged call — who you spoke
 * to, what they said, and the date they now give. It lands in Activity and
 * the order waits on them again until that date. Drafting an email instead
 * is not built yet, so the dialog offers only the call.
 */
export function ContactDialog({
  party,
  today,
  busy,
  onClose,
  onLog,
}: {
  party: ContactParty;
  /** The business date ("2026-10-03"): the earliest date they can give. */
  today: string;
  busy: boolean;
  onClose: () => void;
  onLog: (input: LogSupplierCallRequest) => Promise<OrderStepFailure | null>;
}) {
  const id = useId();
  const [spokenTo, setSpokenTo] = useState("");
  const [note, setNote] = useState("");
  const [expectedOn, setExpectedOn] = useState("");
  const [failure, setFailure] = useState<OrderStepFailure | null>(null);
  const problems: Problems = {
    spoken_to: spokenTo.trim() ? undefined : "Say who you spoke to.",
    note: note.trim() ? undefined : "Say what they told you.",
    expected_on: !expectedOn
      ? "Give the date they now expect."
      : expectedOn < today
        ? "The date cannot be before today."
        : undefined,
  };
  const hasProblem = Object.values(problems).some(Boolean);
  // A field's own check shows once a submit has been tried; the server's wins.
  const errorOf = (field: OrderStepField) =>
    failure?.fieldErrors[field] ?? (failure ? problems[field] : undefined);
  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Contact {party.name}</DialogTitle>
          <DialogDescription>
            Log the call: it lands in Activity, and the order waits on{" "}
            {party.name} again until the date they give.
          </DialogDescription>
        </DialogHeader>
        <form
          className="space-y-3"
          onSubmit={async (e) => {
            e.preventDefault();
            if (hasProblem) {
              setFailure({
                summary: "Fill in each field to log the call.",
                fieldErrors: {},
              });
              return;
            }
            // The page asks when they now expect it: that is when the goods arrive.
            const refused = await onLog({
              spoken_to: spokenTo.trim(),
              note: note.trim(),
              expected_on: expectedOn,
              date_kind: "arrival",
            });
            setFailure(refused);
            if (!refused) onClose();
          }}
        >
          <FailureBanner failure={failure} />
          <section aria-label={`Outstanding with ${party.name}`}>
            <h3 className="text-xs font-medium text-muted-foreground">
              Outstanding with {party.name}
            </h3>
            <ul className="mt-1 list-disc space-y-0.5 pl-5 text-sm">
              {party.outstanding.map((o) => (
                <li key={o}>{o}</li>
              ))}
            </ul>
          </section>
          <Field
            id={`${id}-spoken-to`}
            label="Who you spoke to"
            error={errorOf("spoken_to")}
          >
            {(aria) => (
              <Input
                {...aria}
                value={spokenTo}
                maxLength={120}
                onChange={(e) => setSpokenTo(e.target.value)}
              />
            )}
          </Field>
          <Field
            id={`${id}-note`}
            label="What they said"
            error={errorOf("note")}
          >
            {(aria) => (
              <Textarea
                {...aria}
                rows={3}
                value={note}
                maxLength={2000}
                onChange={(e) => setNote(e.target.value)}
              />
            )}
          </Field>
          <Field
            id={`${id}-expected-on`}
            label="When they now expect it"
            error={errorOf("expected_on")}
          >
            {(aria) => (
              <Input
                {...aria}
                type="date"
                min={today}
                value={expectedOn}
                onChange={(e) => setExpectedOn(e.target.value)}
              />
            )}
          </Field>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={busy}>
              Log the call
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ── The Payment step's steps (step 3a) ────────────────────────────────────

/**
 * Change payment method: the methods the order may move to, as orders
 * offers them (`payment.change_method_choices`). Pro-forma is never one
 * (EA-D5). Moving to card sends the customer the pay link; to account runs
 * the credit gate.
 */
export function ChangePaymentMethodDialog({
  orderNo,
  current,
  choices,
  busy,
  onClose,
  onChange,
}: {
  orderNo: string;
  /** The method's label now: "Credit account". */
  current: string;
  choices: PaymentMethodChoice[];
  busy: boolean;
  onClose: () => void;
  onChange: (
    input: ChangePaymentMethodRequest,
  ) => Promise<OrderStepFailure | null>;
}) {
  const id = useId();
  const [method, setMethod] = useState<string>(choices[0]?.method ?? "");
  const [failure, setFailure] = useState<OrderStepFailure | null>(null);
  const error =
    failure?.fieldErrors.payment_method ??
    failure?.fieldErrors.order_contact_person_id;
  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Change how {orderNo} is paid</DialogTitle>
          <DialogDescription>
            It is paid by {current.toLowerCase()} now. By card, the customer is
            sent a link to pay; on account, credit is checked again.
          </DialogDescription>
        </DialogHeader>
        <form
          className="space-y-3"
          onSubmit={async (e) => {
            e.preventDefault();
            setFailure(null);
            const refused = await onChange({
              payment_method:
                method as ChangePaymentMethodRequest["payment_method"],
            });
            setFailure(refused);
            if (!refused) onClose();
          }}
        >
          <FailureBanner failure={failure} />
          <RadioGroup
            value={method}
            onValueChange={setMethod}
            aria-label="Payment method"
            aria-invalid={error ? true : undefined}
            aria-describedby={error ? `${id}-error` : undefined}
          >
            {choices.map((c) => (
              <div key={c.method} className="flex items-center gap-2">
                <RadioGroupItem value={c.method} id={`${id}-${c.method}`} />
                <Label htmlFor={`${id}-${c.method}`}>{c.label}</Label>
              </div>
            ))}
          </RadioGroup>
          {error && (
            <p id={`${id}-error`} className="text-sm text-red-600">
              {error}
            </p>
          )}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={busy || !method}>
              Change payment method
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/**
 * The picker *Set contact* uses when the page can look the customer's people
 * up (v1 completion D2): the customer's own people (its default order contact
 * first) and anyone else by email. Someone not yet at the customer is found or
 * created there first; nothing is sent to them.
 */
export interface SetContactLookup {
  customerId: string;
  customerName: string;
  suggest: (query: string) => Promise<PortalContactSuggestions>;
  findOrCreate: (choice: PortalContactChoice) => Promise<{ person_id: string }>;
}

/** Set contact: who proofs and the order's emails go to, from the customer's people. */
export function SetContactDialog({
  orderNo,
  people,
  current,
  busy,
  onClose,
  onSet,
  lookup = null,
}: {
  orderNo: string;
  people: OrderContact[];
  current: OrderContact | null;
  busy: boolean;
  onClose: () => void;
  onSet: (input: SetOrderContactRequest) => Promise<OrderStepFailure | null>;
  /** With it, the customer's people are looked up and someone new can be added (D2); without, a list of `people`. */
  lookup?: SetContactLookup | null;
}) {
  const id = useId();
  const [personId, setPersonId] = useState(
    current?.person_id ?? people[0]?.person_id ?? "",
  );
  const startingPerson = people.find((p) => p.person_id === current?.person_id);
  const [choice, setChoice] = useState<PortalContactChoice | null>(
    startingPerson
      ? {
          kind: "person",
          person_id: startingPerson.person_id,
          name: startingPerson.name,
          email: startingPerson.email,
          linked_here: true,
          other_customer_names: [],
        }
      : null,
  );
  const [failure, setFailure] = useState<OrderStepFailure | null>(null);
  const [addFailed, setAddFailed] = useState<string | null>(null);
  const error = failure?.fieldErrors.person_id;

  async function chosenPersonId(): Promise<string | null> {
    if (!lookup) return personId || null;
    if (!choice) return null;
    if (choice.kind === "person" && choice.linked_here) return choice.person_id;
    try {
      return (await lookup.findOrCreate(choice)).person_id;
    } catch {
      setAddFailed(
        `We could not set ${choice.email} up as a person at ${lookup.customerName}. The contact is unchanged.`,
      );
      return null;
    }
  }

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Set the contact for {orderNo}</DialogTitle>
          <DialogDescription>
            Proofs, the pay link and the order's reminders go to them.
          </DialogDescription>
        </DialogHeader>
        <form
          className="space-y-3"
          onSubmit={async (e) => {
            e.preventDefault();
            setFailure(null);
            setAddFailed(null);
            const chosen = await chosenPersonId();
            if (!chosen) return;
            const refused = await onSet({ person_id: chosen });
            setFailure(refused);
            if (!refused) onClose();
          }}
        >
          <FailureBanner failure={failure} />
          {addFailed && (
            <p role="alert" className="text-sm text-red-600">
              {addFailed}
            </p>
          )}
          {lookup ? (
            <PortalContactPicker
              id={`${id}-picker`}
              customerName={lookup.customerName}
              linkedPeople={people.map((p) => ({
                person_id: p.person_id,
                name: p.name,
                email: p.email,
              }))}
              value={choice}
              onChange={setChoice}
              suggest={lookup.suggest}
              whenActedOn="set the contact"
              aria-invalid={error ? true : undefined}
              aria-describedby={error ? `${id}-error` : undefined}
            />
          ) : people.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              The customer has no people to choose from yet.
            </p>
          ) : (
            <RadioGroup
              value={personId}
              onValueChange={setPersonId}
              aria-label="Contact"
              aria-invalid={error ? true : undefined}
              aria-describedby={error ? `${id}-error` : undefined}
            >
              {people.map((p) => (
                <div key={p.person_id} className="flex items-center gap-2">
                  <RadioGroupItem
                    value={p.person_id}
                    id={`${id}-${p.person_id}`}
                  />
                  <Label htmlFor={`${id}-${p.person_id}`}>
                    {p.name}{" "}
                    <span className="font-normal text-muted-foreground">
                      {p.email}
                    </span>
                  </Label>
                </div>
              ))}
            </RadioGroup>
          )}
          {error && (
            <p id={`${id}-error`} className="text-sm text-red-600">
              {error}
            </p>
          )}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={busy || (lookup ? !choice : !personId)}
            >
              Set contact
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/** Record PO number: the customer's PO number, owed under their PO rule ("PO to follow"). */
export function RecordPoNumberDialog({
  customerName,
  poFormatHint = null,
  busy,
  onClose,
  onRecord,
}: {
  customerName: string;
  /** "Like 4500000000 (45 then 8 digits)": the payer's PO format, as credit control words it; null without one. */
  poFormatHint?: string | null;
  busy: boolean;
  onClose: () => void;
  onRecord: (
    input: CustomerOrderNumberRequest,
  ) => Promise<OrderStepFailure | null>;
}) {
  const id = useId();
  const [number, setNumber] = useState("");
  const [failure, setFailure] = useState<OrderStepFailure | null>(null);
  const clean = number.trim();
  const problem = !clean
    ? "Enter the PO number."
    : clean.length > 50
      ? "A PO number is at most 50 characters."
      : undefined;
  const error =
    failure?.fieldErrors.customer_order_number ??
    (failure ? problem : undefined);
  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add {customerName}'s PO number</DialogTitle>
          <DialogDescription>
            Their PO rule wants one before release (PO to follow).
          </DialogDescription>
        </DialogHeader>
        <form
          className="space-y-3"
          onSubmit={async (e) => {
            e.preventDefault();
            if (problem) {
              setFailure({
                summary: "Fill in the PO number to add it.",
                fieldErrors: {},
              });
              return;
            }
            const refused = await onRecord({ customer_order_number: clean });
            setFailure(refused);
            if (!refused) onClose();
          }}
        >
          <FailureBanner failure={failure} />
          <Field
            id={`${id}-number`}
            label="PO number"
            hint={poFormatHint ?? undefined}
            error={error}
          >
            {(aria) => (
              <Input
                {...aria}
                value={number}
                placeholder="PO-4580"
                onChange={(e) => setNumber(e.target.value)}
              />
            )}
          </Field>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={busy}>
              Add PO number
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Record job number (Q3): a made line released record-only waits for the CSR
 * to raise its Tharstern job by hand; this records (or corrects) its number.
 * Orders checks the number's shape and binds a wrong one to the field.
 */
export function RecordTharsternJobDialog({
  lineLabel,
  currentJobNo,
  busy,
  onClose,
  onRecord,
}: {
  /** The line as the order shows it: `.01 Event programmes`. */
  lineLabel: string;
  /** The number already recorded, when correcting it. */
  currentJobNo: string | null;
  busy: boolean;
  onClose: () => void;
  onRecord: (
    input: RecordTharsternJobRequest,
  ) => Promise<OrderStepFailure | null>;
}) {
  const id = useId();
  const [jobNo, setJobNo] = useState(currentJobNo ?? "");
  const [failure, setFailure] = useState<OrderStepFailure | null>(null);
  const clean = jobNo.trim();
  const problem = !clean ? "Enter the Tharstern job number." : undefined;
  const error =
    failure?.fieldErrors.tharstern_job_no ?? (failure ? problem : undefined);
  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Record the Tharstern job for {lineLabel}</DialogTitle>
          <DialogDescription>
            Raise the job in Tharstern by hand, then record its number here.
          </DialogDescription>
        </DialogHeader>
        <form
          className="space-y-3"
          onSubmit={async (e) => {
            e.preventDefault();
            if (problem) {
              setFailure({
                summary: "Fill in the job number to record it.",
                fieldErrors: {},
              });
              return;
            }
            const refused = await onRecord({ tharstern_job_no: clean });
            setFailure(refused);
            if (!refused) onClose();
          }}
        >
          <FailureBanner failure={failure} />
          <Field id={`${id}-job`} label="Tharstern job number" error={error}>
            {(aria) => (
              <Input
                {...aria}
                value={jobNo}
                inputMode="decimal"
                placeholder="740715"
                onChange={(e) => setJobNo(e.target.value)}
              />
            )}
          </Field>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={busy}>
              Record job number
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Set VAT (finance, sales ledger SL2): the treatment of a line the dictionary
 * gave none, with a reason. CIS never assumes one; the line's invoice issues
 * once nothing else holds it.
 */
export function SetVatDialog({
  lineNo,
  options,
  busy,
  onClose,
  onSet,
}: {
  /** ".02" */
  lineNo: string;
  /** The backend's choices and words (`billing.vat_treatment_options`). */
  options: VatTreatmentOption[];
  busy: boolean;
  onClose: () => void;
  onSet: (input: SetVatTreatmentRequest) => Promise<OrderStepFailure | null>;
}) {
  const id = useId();
  const [treatment, setTreatment] = useState("");
  const [reason, setReason] = useState("");
  const [failure, setFailure] = useState<OrderStepFailure | null>(null);
  const problems = {
    vat_treatment: treatment ? undefined : "Choose a VAT treatment.",
    reason: !reason.trim()
      ? "Enter the reason."
      : reason.trim().length > 500
        ? "The reason can be at most 500 characters."
        : undefined,
  };
  const errorOf = (field: "vat_treatment" | "reason") =>
    failure?.fieldErrors[field] ?? (failure ? problems[field] : undefined);
  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Set VAT on line {lineNo}</DialogTitle>
          <DialogDescription>
            The order line&rsquo;s dictionary item has no VAT treatment, and One
            never assumes a VAT treatment. The invoice will be raised once every
            due order line has a VAT treatment.
          </DialogDescription>
        </DialogHeader>
        <form
          className="space-y-3"
          onSubmit={async (e) => {
            e.preventDefault();
            if (problems.vat_treatment || problems.reason) {
              setFailure({
                summary: "Choose a treatment and say why to set it.",
                fieldErrors: {},
              });
              return;
            }
            const refused = await onSet({
              vat_treatment: treatment,
              reason: reason.trim(),
            });
            setFailure(refused);
            if (!refused) onClose();
          }}
        >
          <FailureBanner failure={failure} />
          <Field
            id={`${id}-treatment`}
            label="VAT treatment"
            error={errorOf("vat_treatment")}
          >
            {(aria) => (
              <RadioGroup
                {...aria}
                value={treatment}
                onValueChange={setTreatment}
              >
                {options.map((o) => (
                  <div key={o.value} className="flex items-center gap-2">
                    <RadioGroupItem value={o.value} id={`${id}-${o.value}`} />
                    <Label htmlFor={`${id}-${o.value}`}>{o.label}</Label>
                  </div>
                ))}
              </RadioGroup>
            )}
          </Field>
          <Field id={`${id}-reason`} label="Reason" error={errorOf("reason")}>
            {(aria) => (
              <Textarea
                {...aria}
                value={reason}
                rows={2}
                placeholder="A printed book: zero-rated"
                onChange={(e) => setReason(e.target.value)}
              />
            )}
          </Field>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={busy}>
              Set VAT
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ── Record payment (finance) ──────────────────────────────────────────────

/** What Record payment reads of a pro-forma: the order page's or the Awaiting payment list's row. */
export interface RecordablePayment {
  proforma_no: string;
  outstanding_pence: number;
  issued_on: string;
  /** The bank transfer's reference: the `PF-` number. */
  payment_reference: string;
}

/**
 * Record payment against a pro-forma (round 19's mock-up): what arrived, the
 * day it arrived, and the bank line's reference, as Xero's bank feed shows
 * them. Less than what is owed leaves it part paid; more is recorded as
 * received and finance refunds the excess in Xero (doc 50 §17 Q5, Q6).
 */
export function RecordPaymentDialog({
  proforma,
  busy,
  onClose,
  onRecord,
}: {
  proforma: RecordablePayment;
  busy: boolean;
  onClose: () => void;
  onRecord: (
    input: RecordProformaPaymentRequest,
  ) => Promise<OrderStepFailure | null>;
}) {
  const id = useId();
  const today = londonTodayIso();
  const owed = proforma.outstanding_pence;
  const [amount, setAmount] = useState((owed / 100).toFixed(2));
  const [receivedOn, setReceivedOn] = useState(today);
  const [bankReference, setBankReference] = useState("");
  const [failure, setFailure] = useState<OrderStepFailure | null>(null);
  const pence = penceOf(amount);
  const problems: Problems = {
    amount_pence:
      pence === null || pence <= 0
        ? "Enter the amount received, e.g. 768.00."
        : undefined,
    received_on: !receivedOn
      ? "Enter the date the money arrived."
      : receivedOn > today
        ? "The date can't be in the future."
        : undefined,
    bank_reference: !bankReference.trim()
      ? "Enter the bank line's reference, from Xero's bank feed."
      : undefined,
  };
  const problemCount = Object.values(problems).filter(Boolean).length;
  const errorOf = (field: OrderStepField) =>
    failure?.fieldErrors[field] ?? (failure ? problems[field] : undefined);
  const isShort = pence !== null && pence > 0 && pence < owed;
  const isOver = pence !== null && pence > owed;
  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            Record payment against {proforma.proforma_no}
          </DialogTitle>
          <DialogDescription>
            Finance only. Match the bank line in Xero&apos;s bank feed by its
            reference, {proforma.payment_reference}. Once it is paid in full,
            One will raise the VAT invoice, marked paid, and the order will go
            ahead.
          </DialogDescription>
        </DialogHeader>
        <form
          className="space-y-3"
          noValidate
          onSubmit={async (e) => {
            e.preventDefault();
            if (problemCount > 0) {
              setFailure({
                summary:
                  problemCount === 1
                    ? "One field needs correcting."
                    : `${problemCount} fields need correcting.`,
                fieldErrors: {},
              });
              return;
            }
            const refused = await onRecord({
              amount_pence: pence!,
              received_on: receivedOn,
              bank_reference: bankReference.trim(),
            });
            setFailure(refused);
            if (!refused) onClose();
          }}
        >
          <FailureBanner failure={failure} />
          <div className="grid gap-3 sm:grid-cols-2">
            <Field
              id={`${id}-amount`}
              label="Amount received (£)"
              error={errorOf("amount_pence")}
            >
              {(aria) => (
                <Input
                  {...aria}
                  inputMode="decimal"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                />
              )}
            </Field>
            <Field
              id={`${id}-received-on`}
              label="Received on"
              error={errorOf("received_on")}
            >
              {(aria) => (
                <Input
                  {...aria}
                  type="date"
                  value={receivedOn}
                  min={proforma.issued_on}
                  max={today}
                  onChange={(e) => setReceivedOn(e.target.value)}
                />
              )}
            </Field>
          </div>
          <Field
            id={`${id}-bank-reference`}
            label="Bank reference"
            error={errorOf("bank_reference")}
          >
            {(aria) => (
              <Input
                {...aria}
                value={bankReference}
                maxLength={64}
                onChange={(e) => setBankReference(e.target.value)}
              />
            )}
          </Field>
          <p className="text-xs text-muted-foreground">
            {formatQuotedPrice(owed)} owed, issued{" "}
            {formatShortDateOrDash(proforma.issued_on)}.
            {isShort &&
              " Less than that leaves it part paid, and the order waits until it is paid in full."}
            {isOver &&
              " More than that is recorded as received: refund the excess in Xero."}
          </p>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={busy}>
              Record payment
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Contact {customer}, once their reminders have run out (doc 43 §7.2): what
 * they owe, then the logged call. It restarts every reminder clock.
 */
export function ContactCustomerDialog({
  party,
  busy,
  onClose,
  onLog,
}: {
  party: CustomerContactParty;
  busy: boolean;
  onClose: () => void;
  onLog: (input: LogContactRequest) => Promise<OrderStepFailure | null>;
}) {
  const id = useId();
  const [spokenTo, setSpokenTo] = useState("");
  const [note, setNote] = useState("");
  const [failure, setFailure] = useState<OrderStepFailure | null>(null);
  const problems: Problems = {
    spoken_to: spokenTo.trim() ? undefined : "Say who you spoke to.",
    note: note.trim() ? undefined : "Say what they told you.",
  };
  const hasProblem = Object.values(problems).some(Boolean);
  const errorOf = (field: OrderStepField) =>
    failure?.fieldErrors[field] ?? (failure ? problems[field] : undefined);
  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Contact {party.name}</DialogTitle>
          <DialogDescription>
            Log the call: it lands in Activity, and their reminders start again.
          </DialogDescription>
        </DialogHeader>
        <form
          className="space-y-3"
          onSubmit={async (e) => {
            e.preventDefault();
            if (hasProblem) {
              setFailure({
                summary: "Fill in each field to log the call.",
                fieldErrors: {},
              });
              return;
            }
            const refused = await onLog({
              spoken_to: spokenTo.trim(),
              note: note.trim(),
            });
            setFailure(refused);
            if (!refused) onClose();
          }}
        >
          <FailureBanner failure={failure} />
          <section aria-label={`What ${party.name} owes`}>
            <h3 className="text-xs font-medium text-muted-foreground">
              What {party.name} owes
            </h3>
            <ul className="mt-1 list-disc space-y-0.5 pl-5 text-sm">
              {party.outstanding.map((o) => (
                <li key={o}>{o}</li>
              ))}
            </ul>
          </section>
          <Field
            id={`${id}-spoken-to`}
            label="Who you spoke to"
            error={errorOf("spoken_to")}
          >
            {(aria) => (
              <Input
                {...aria}
                value={spokenTo}
                maxLength={120}
                onChange={(e) => setSpokenTo(e.target.value)}
              />
            )}
          </Field>
          <Field
            id={`${id}-note`}
            label="What they said"
            error={errorOf("note")}
          >
            {(aria) => (
              <Textarea
                {...aria}
                rows={3}
                value={note}
                maxLength={2000}
                onChange={(e) => setNote(e.target.value)}
              />
            )}
          </Field>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={busy}>
              Log the call
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/** Orders' bound on a cancellation's reason (`CancelLineRequest`). */
const MAX_CANCEL_REASON_LENGTH = 500;

/** "250", "250.5" or "250.50" -> 25000 pence; null when it isn't an amount. */
function penceOfPounds(pounds: string): number | null {
  const text = pounds.trim().replace(/^£/, "").replace(/,/g, "");
  if (!/^\d+(\.\d{1,2})?$/.test(text)) return null;
  return Math.round(Number(text) * 100);
}

/**
 * Cancel the rest (doc 98 §3, §8): on a part-sent line, cancel what is not yet
 * sent. Every sentence is the backend's (`CancelRestView`): what was sent, what
 * the supplier or stock is told, and where the money goes. A bought line going
 * to several addresses may split the rest across them; a bought line may record
 * the amount agreed with the supplier. Posted to the line's `cancel_url` with
 * the quantity shown, so a stale page is refused with the new figure.
 */
export function CancelRestDialog({
  rest,
  busy,
  onClose,
  onConfirm,
}: {
  rest: CancelRestView;
  busy: boolean;
  onClose: () => void;
  onConfirm: (input: CancelLineRequest) => Promise<OrderStepFailure | null>;
}) {
  const id = useId();
  const [reason, setReason] = useState("");
  const [shares, setShares] = useState<Record<string, string>>(() =>
    Object.fromEntries(
      rest.shares.map((s) => [s.delivery_line_id, String(s.quantity)]),
    ),
  );
  const [agreedAmount, setAgreedAmount] = useState("");
  const [agreedReason, setAgreedReason] = useState("");
  const [failure, setFailure] = useState<OrderStepFailure | null>(null);

  const clean = reason.trim();
  const split = rest.shares.map((s) => ({
    share: s,
    quantity: /^\d+$/.test(shares[s.delivery_line_id] ?? "")
      ? Number(shares[s.delivery_line_id])
      : null,
  }));
  const agreedPence = agreedAmount.trim() ? penceOfPounds(agreedAmount) : null;
  const problems: Problems = {
    reason: !clean
      ? "Say why the rest is cancelled."
      : clean.length > MAX_CANCEL_REASON_LENGTH
        ? `A reason is at most ${MAX_CANCEL_REASON_LENGTH} characters.`
        : undefined,
    shares:
      rest.can_split &&
      split.reduce((sum, s) => sum + (s.quantity ?? 0), 0) !== rest.quantity
        ? `The addresses must add up to the ${rest.quantity.toLocaleString("en-GB")} cancelled.`
        : undefined,
    agreed_amount_pence:
      agreedAmount.trim() && agreedPence === null
        ? "Enter the amount, e.g. 150.00."
        : undefined,
    agreed_amount_reason:
      agreedAmount.trim() && !agreedReason.trim()
        ? "Say why that amount was agreed."
        : undefined,
  };
  split.forEach(({ share, quantity }, position) => {
    problems[`shares.${position}.quantity`] = !rest.can_split
      ? undefined
      : quantity === null
        ? "Enter a whole number."
        : quantity > share.most
          ? `At most ${share.most.toLocaleString("en-GB")} can come off this address.`
          : undefined;
  });
  const problemCount = Object.values(problems).filter(Boolean).length;
  const errorOf = (field: OrderStepField) =>
    failure?.fieldErrors[field] ?? (failure ? problems[field] : undefined);

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{rest.title}</DialogTitle>
          <DialogDescription asChild>
            <div className="space-y-1">
              {rest.sentences.map((sentence) => (
                <p key={sentence}>{sentence}</p>
              ))}
            </div>
          </DialogDescription>
        </DialogHeader>
        <form
          className="space-y-3"
          onSubmit={async (e) => {
            e.preventDefault();
            if (problemCount > 0) {
              setFailure({
                summary:
                  problemCount === 1
                    ? "One field needs correcting."
                    : `${problemCount} fields need correcting.`,
                fieldErrors: {},
              });
              return;
            }
            const refused = await onConfirm({
              reason: clean,
              quantity: rest.quantity,
              shares: rest.can_split
                ? split.map(({ share, quantity }) => ({
                    delivery_line_id: share.delivery_line_id,
                    quantity: quantity ?? 0,
                  }))
                : null,
              agreed_amount_pence: agreedPence,
              agreed_amount_reason:
                agreedPence !== null ? agreedReason.trim() : null,
            });
            setFailure(refused);
            if (!refused) onClose();
          }}
        >
          {rest.half_done_notice && (
            <Alert role="status">
              <AlertDescription>{rest.half_done_notice}</AlertDescription>
            </Alert>
          )}
          <FailureBanner failure={failure} />
          {rest.can_split && (
            <fieldset className="space-y-2">
              <legend className="text-sm font-medium">
                What each address loses
              </legend>
              {split.map(({ share }, position) => (
                <Field
                  key={share.delivery_line_id}
                  id={`${id}-share-${position}`}
                  label={`${share.destination} (at most ${share.most.toLocaleString("en-GB")})`}
                  error={errorOf(`shares.${position}.quantity`)}
                >
                  {(aria) => (
                    <Input
                      {...aria}
                      inputMode="numeric"
                      value={shares[share.delivery_line_id] ?? ""}
                      onChange={(e) =>
                        setShares((current) => ({
                          ...current,
                          [share.delivery_line_id]: e.target.value,
                        }))
                      }
                    />
                  )}
                </Field>
              ))}
              {errorOf("shares") && (
                <p className="text-sm text-red-600">{errorOf("shares")}</p>
              )}
            </fieldset>
          )}
          {rest.agreed_amount_label && (
            <>
              <Field
                id={`${id}-agreed`}
                label={`${rest.agreed_amount_label} (optional, ex VAT)`}
                error={errorOf("agreed_amount_pence")}
                hint="Leave it blank to cut the cost by the part not sent."
              >
                {(aria) => (
                  <Input
                    {...aria}
                    inputMode="decimal"
                    placeholder="£"
                    value={agreedAmount}
                    onChange={(e) => setAgreedAmount(e.target.value)}
                  />
                )}
              </Field>
              {agreedAmount.trim() && (
                <Field
                  id={`${id}-agreed-reason`}
                  label="Reason"
                  error={errorOf("agreed_amount_reason")}
                >
                  {(aria) => (
                    <Input
                      {...aria}
                      value={agreedReason}
                      onChange={(e) => setAgreedReason(e.target.value)}
                    />
                  )}
                </Field>
              )}
            </>
          )}
          <Field
            id={`${id}-reason`}
            label={rest.reason_label}
            error={errorOf("reason")}
          >
            {(aria) => (
              <Textarea
                {...aria}
                rows={3}
                value={reason}
                placeholder="The supplier ran out of board"
                onChange={(e) => setReason(e.target.value)}
              />
            )}
          </Field>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Back
            </Button>
            <Button type="submit" disabled={busy}>
              {rest.confirm_label}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/** A refused Cancel line: its summary and the reason field's error. */
export interface CancelLineFailure {
  summary: string;
  fieldErrors: { reason?: string };
}

/**
 * Cancel line (3c, doc 43 §6.2): why, then confirm. Posted to orders' route
 * for the line, from the order page; the line stays on the order, struck
 * through with the reason.
 */
export function CancelLineDialog({
  title,
  busy,
  onClose,
  onConfirm,
}: {
  /** "Cancel line .01". */
  title: string;
  busy: boolean;
  onClose: () => void;
  onConfirm: (reason: string) => Promise<CancelLineFailure | null>;
}) {
  const id = useId();
  const [reason, setReason] = useState("");
  const [failure, setFailure] = useState<CancelLineFailure | null>(null);
  const clean = reason.trim();
  const problem = !clean
    ? "Say why the line is cancelled."
    : clean.length > MAX_CANCEL_REASON_LENGTH
      ? `A reason is at most ${MAX_CANCEL_REASON_LENGTH} characters.`
      : undefined;
  const error = failure?.fieldErrors.reason ?? (failure ? problem : undefined);
  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>
            The line stays on the order, struck through, and a card payment for
            it is refunded. This can&apos;t be undone; add the line again if
            needed.
          </DialogDescription>
        </DialogHeader>
        <form
          className="space-y-3"
          onSubmit={async (e) => {
            e.preventDefault();
            if (problem) {
              setFailure({
                summary: "Say why the line is cancelled to cancel it.",
                fieldErrors: {},
              });
              return;
            }
            const refused = await onConfirm(clean);
            setFailure(refused);
            if (!refused) onClose();
          }}
        >
          {failure && (
            <Alert variant="destructive" role="alert">
              <AlertDescription>{failure.summary}</AlertDescription>
            </Alert>
          )}
          <Field id={`${id}-reason`} label="Why is it cancelled" error={error}>
            {(aria) => (
              <Textarea
                {...aria}
                rows={3}
                value={reason}
                placeholder="The customer no longer needs the Leeds version"
                onChange={(e) => setReason(e.target.value)}
              />
            )}
          </Field>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Back
            </Button>
            <Button type="submit" disabled={busy}>
              Cancel line
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
